/**
 * Visit Detail Page - Sprint 4
 * Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { visitsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  RefreshCw, ChevronLeft, Save, CheckCircle2, XCircle, 
  AlertTriangle, FileText, DollarSign, Clock, User, Archive
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Scheduled: 'bg-blue-100 text-blue-700 border-blue-300',
  InProgress: 'bg-amber-100 text-amber-700 border-amber-300',
  Completed: 'bg-green-100 text-green-700 border-green-300',
  Cancelled: 'bg-slate-100 text-slate-600 border-slate-300',
  NoShow: 'bg-red-100 text-red-700 border-red-300'
};

const VERIFICATION_METHODS = [
  { value: 'Signature', label: 'Signature' },
  { value: 'Voice', label: 'Voice' },
  { value: 'Other', label: 'Other' },
  { value: 'None', label: 'None (requires exception reason)' }
];

const VisitDetailPage = () => {
  const navigate = useNavigate();
  const { visitId } = useParams();
  const { user, logout, loading: authLoading, canCompleteVisits, canEditNotes, canMarkBillable } = useAuth();
  
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Note form state
  const [noteData, setNoteData] = useState({
    observations: '',
    activities_completed: '',
    activities_not_completed: '',
    reason_not_completed: '',
    client_verification_method: 'Signature',
    verification_exception_reason: '',
    signed_by: '',
    signed_at: ''
  });
  
  // Billable check state
  const [billableCheck, setBillableCheck] = useState(null);
  
  // Dialog states
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchVisit = useCallback(async () => {
    setLoading(true);
    try {
      const response = await visitsAPI.get(visitId);
      setVisit(response.data);
      
      // Populate note form if note exists
      if (response.data.note) {
        const n = response.data.note;
        setNoteData({
          observations: n.observations || '',
          activities_completed: (n.activities_completed || []).join('\n'),
          activities_not_completed: (n.activities_not_completed || []).join('\n'),
          reason_not_completed: n.reason_not_completed || '',
          client_verification_method: n.client_verification_method || 'Signature',
          verification_exception_reason: n.verification_exception_reason || '',
          signed_by: n.signed_by || '',
          signed_at: n.signed_at ? new Date(n.signed_at).toISOString().slice(0, 16) : ''
        });
      }
    } catch (err) {
      toast.error('Failed to load visit');
      navigate('/schedule');
    } finally {
      setLoading(false);
    }
  }, [visitId, navigate]);

  const fetchBillableCheck = async () => {
    try {
      const response = await visitsAPI.checkBillable(visitId);
      setBillableCheck(response.data);
    } catch (err) {
      console.error('Failed to check billable status', err);
    }
  };

  useEffect(() => {
    fetchVisit();
  }, [fetchVisit]);

  useEffect(() => {
    if (visit && visit.status === 'Completed' && !visit.billable_flag) {
      fetchBillableCheck();
    }
  }, [visit]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSaveNote = async () => {
    if (!noteData.observations.trim()) {
      toast.error('Observations are required');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        observations: noteData.observations,
        activities_completed: noteData.activities_completed.split('\n').filter(a => a.trim()),
        activities_not_completed: noteData.activities_not_completed.split('\n').filter(a => a.trim()),
        reason_not_completed: noteData.reason_not_completed || null,
        client_verification_method: noteData.client_verification_method,
        verification_exception_reason: noteData.verification_exception_reason || null,
        signed_by: noteData.signed_by || null,
        signed_at: noteData.signed_at ? new Date(noteData.signed_at).toISOString() : null
      };
      
      await visitsAPI.createOrUpdateNote(visitId, data);
      toast.success('Visit note saved');
      fetchVisit();
      fetchBillableCheck();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await visitsAPI.complete(visitId);
      toast.success('Visit marked as completed');
      fetchVisit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete visit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkBillable = async () => {
    setActionLoading(true);
    try {
      await visitsAPI.markBillable(visitId);
      toast.success('Visit marked as billable');
      fetchVisit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark billable');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    if (archiveReason.length < 10) {
      toast.error('Archive reason must be at least 10 characters');
      return;
    }
    
    setActionLoading(true);
    try {
      await visitsAPI.archive(visitId, archiveReason);
      toast.success('Visit archived');
      navigate('/schedule');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive visit');
    } finally {
      setActionLoading(false);
      setShowArchiveDialog(false);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric', 
      hour: 'numeric', minute: '2-digit' 
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!visit) return null;

  const isArchived = !!visit.archived_at;
  const isCompleted = visit.status === 'Completed';
  const isBillable = visit.billable_flag;
  const hasNote = visit.has_note;
  const isCaregiver = user?.role === 'Caregiver';

  return (
    <Layout title="Visit Details">
      <div data-testid="visit-detail-page">
        {/* Back button and status badges */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/schedule')}>
              <ChevronLeft className="h-4 w-4 mr-1" />Schedule
            </Button>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_COLORS[visit.status]}>{visit.status}</Badge>
              {isBillable && <Badge className="bg-green-100 text-green-700"><DollarSign className="h-3 w-3 mr-1" />Billable</Badge>}
              {hasNote && <Badge variant="outline" className="text-avida-blue"><FileText className="h-3 w-3 mr-1" />Note</Badge>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Visit Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-slate-400" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Scheduled</Label>
                  <p className="font-medium">{formatDateTime(visit.scheduled_start)}</p>
                  <p className="text-sm text-slate-500">to {formatDateTime(visit.scheduled_end)}</p>
                </div>
                {visit.actual_start && (
                  <div>
                    <Label className="text-xs text-slate-500">Actual</Label>
                    <p className="font-medium">{formatDateTime(visit.actual_start)}</p>
                    <p className="text-sm text-slate-500">to {formatDateTime(visit.actual_end)}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Service Type</Label>
                  <p className="font-medium">{visit.service_type}</p>
                </div>
                {visit.location_note && (
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <p className="text-sm">{visit.location_note}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-slate-400" />
                  Participants
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Client</Label>
                  <p className="font-medium">{visit.client_name || visit.client_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Caregiver</Label>
                  <p className="font-medium">{visit.caregiver_name || visit.caregiver_id}</p>
                </div>
              </CardContent>
            </Card>

            {/* Actions Card */}
            {!isArchived && !isCaregiver && (
              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!isCompleted && canCompleteVisits && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleComplete}
                      disabled={actionLoading || !hasNote}
                      data-testid="complete-visit-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark Completed
                    </Button>
                  )}
                  {!isCompleted && !hasNote && (
                    <p className="text-xs text-amber-600 text-center">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Visit note required before completing
                    </p>
                  )}
                  
                  {isCompleted && !isBillable && canMarkBillable && (
                    <>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={handleMarkBillable}
                        disabled={actionLoading || (billableCheck && !billableCheck.can_bill)}
                        data-testid="mark-billable-btn"
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Mark Billable
                      </Button>
                      {billableCheck && !billableCheck.can_bill && (
                        <div className="text-xs text-red-600 p-2 bg-red-50 rounded">
                          <p className="font-medium">Cannot bill:</p>
                          <ul className="list-disc list-inside">
                            {billableCheck.missing_requirements.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                  
                  {isBillable && (
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto mb-1" />
                      <p className="text-green-800 font-medium">Billable</p>
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setShowArchiveDialog(true)}
                    data-testid="archive-visit-btn"
                  >
                    <Archive className="h-4 w-4 mr-2" />Archive Visit
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column - Visit Note */}
          <div className="lg:col-span-2">
            <Card data-testid="visit-note-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-slate-400" />
                      Visit Note
                    </CardTitle>
                    <CardDescription>
                      {hasNote ? 'Record of care documentation' : 'No note yet - create one below'}
                    </CardDescription>
                  </div>
                  {canEditNotes && !isArchived && (
                    <Button onClick={handleSaveNote} disabled={saving} data-testid="save-note-btn">
                      {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Note
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isCaregiver ? (
                  // Read-only view for caregivers
                  visit.note ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-slate-500">Observations</Label>
                        <p className="mt-1 p-3 bg-slate-50 rounded-lg">{visit.note.observations}</p>
                      </div>
                      {visit.note.activities_completed.length > 0 && (
                        <div>
                          <Label className="text-xs text-slate-500">Activities Completed</Label>
                          <ul className="mt-1 p-3 bg-slate-50 rounded-lg list-disc list-inside">
                            {visit.note.activities_completed.map((a, i) => <li key={i}>{a}</li>)}
                          </ul>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-slate-500">Client Verification</Label>
                        <p className="mt-1">{visit.note.client_verification_method}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No visit note available</p>
                  )
                ) : (
                  // Editable form for office staff
                  <>
                    <div>
                      <Label>Observations *</Label>
                      <Textarea 
                        value={noteData.observations}
                        onChange={(e) => setNoteData({...noteData, observations: e.target.value})}
                        placeholder="Document observations from the visit..."
                        className="min-h-[100px]"
                        disabled={isArchived}
                        data-testid="note-observations"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Activities Completed</Label>
                        <Textarea 
                          value={noteData.activities_completed}
                          onChange={(e) => setNoteData({...noteData, activities_completed: e.target.value})}
                          placeholder="One activity per line..."
                          className="min-h-[80px]"
                          disabled={isArchived}
                          data-testid="note-activities-completed"
                        />
                      </div>
                      <div>
                        <Label>Activities Not Completed</Label>
                        <Textarea 
                          value={noteData.activities_not_completed}
                          onChange={(e) => setNoteData({...noteData, activities_not_completed: e.target.value})}
                          placeholder="One activity per line..."
                          className="min-h-[80px]"
                          disabled={isArchived}
                          data-testid="note-activities-not-completed"
                        />
                      </div>
                    </div>
                    
                    {noteData.activities_not_completed.trim() && (
                      <div>
                        <Label>Reason for Incomplete Activities *</Label>
                        <Textarea 
                          value={noteData.reason_not_completed}
                          onChange={(e) => setNoteData({...noteData, reason_not_completed: e.target.value})}
                          placeholder="Explain why activities were not completed..."
                          disabled={isArchived}
                          data-testid="note-reason-not-completed"
                        />
                        <p className="text-xs text-amber-600 mt-1">
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          Required when activities are not completed
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Client Verification Method *</Label>
                        <Select 
                          value={noteData.client_verification_method} 
                          onValueChange={(v) => setNoteData({...noteData, client_verification_method: v})}
                          disabled={isArchived}
                        >
                          <SelectTrigger data-testid="note-verification-method">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VERIFICATION_METHODS.map(m => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {noteData.client_verification_method === 'None' && (
                        <div>
                          <Label>Verification Exception Reason *</Label>
                          <Input 
                            value={noteData.verification_exception_reason}
                            onChange={(e) => setNoteData({...noteData, verification_exception_reason: e.target.value})}
                            placeholder="Explain why no verification..."
                            disabled={isArchived}
                            data-testid="note-exception-reason"
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Signed By</Label>
                        <Input 
                          value={noteData.signed_by}
                          onChange={(e) => setNoteData({...noteData, signed_by: e.target.value})}
                          placeholder="Name of signer"
                          disabled={isArchived}
                          data-testid="note-signed-by"
                        />
                      </div>
                      <div>
                        <Label>Signed At</Label>
                        <Input 
                          type="datetime-local"
                          value={noteData.signed_at}
                          onChange={(e) => setNoteData({...noteData, signed_at: e.target.value})}
                          disabled={isArchived}
                          data-testid="note-signed-at"
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Archive Dialog */}
        <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Archive Visit</DialogTitle>
              <DialogDescription>This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Archive Reason * (min 10 characters)</Label>
                <Textarea 
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="Enter reason for archiving..."
                  data-testid="archive-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
              <Button 
                variant="destructive"
                onClick={handleArchive} 
                disabled={actionLoading || archiveReason.length < 10}
                data-testid="confirm-archive"
              >
                {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Archive Visit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default VisitDetailPage;
